import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import DocumentProcessingState from "documents/classification/_views/document-processing-state";
import { DocumentContentAccess } from "documents/classification/document-content-access";
import Document from "documents/document";
import { subjectMatterRefusal } from "documents/downloads/document-download-withholding";
import { DocumentFileScanStatus } from "documents/downloads/document-file-scan-status";
import { DocumentSubjectMatter } from "permissions/resources/documents/document-subject-matter";

const SubjectMatterChips = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(0.5),
  marginTop: theme.spacing(0.25),
}));
/**
 * Under a document's name: why it cannot be answered from yet, or once it is processed, its subject
 * matters (#6986). The Documents page draws the same two things, in columns this list has no room for.
 *
 * Two more say why the row cannot be opened (#6992): a file the malware scan withholds, which says
 * so in place of its processing, and a document the reader may not read for its subject matter, which
 * says "Restricted" and names what it needs, as the Documents page's Subject matter does.
 */
export default function WorkDocumentState(props: Readonly<{ document: Document }>) {
  const { document } = props;
  const scanWithholds = document.scanStatus !== undefined && document.scanStatus !== DocumentFileScanStatus.Clean;
  if (scanWithholds || document.needsProcessingExplained) {
    return <DocumentProcessingState status={document.classificationStatus} scanStatus={document.scanStatus} />;
  }
  if (document.contentAccess === DocumentContentAccess.SubjectMatterRestricted) {
    return (
      <SubjectMatterChips aria-label="Subject matter">
        <Tooltip describeChild title={subjectMatterRefusal(document.restrictedSubjectMatters)}>
          <Chip size="small" variant="outlined" icon={<LockOutlinedIcon />} label="Restricted" tabIndex={0} />
        </Tooltip>
      </SubjectMatterChips>
    );
  }
  if (!document.isProcessed || !document.subjectMatters?.length) return null;

  return (
    <SubjectMatterChips aria-label="Subject matter">
      {document.subjectMatters.map((subjectMatter) => (
        <Chip key={subjectMatter} size="small" label={DocumentSubjectMatter[subjectMatter]} />
      ))}
    </SubjectMatterChips>
  );
}
